<template>
    <div class="anestesi">
        <admin-lock-border />
        <modal-basic ref="dialog" />
        <h2 v-if="props.parameters.redigera">Redigera anestesi</h2>
        <h2 v-else>Skapa ny anestesi</h2>
        <div class="form">
            <FormulateForm v-model="formData" @submit="createAnestesi">
                <div class="grid-container">
                    <FormulateInput
                        type="text"
                        name="displayName"
                        label="Namn *"
                        validation="required"
                        validation-name="Namn"
                    >
                    </FormulateInput>
                    <FormulateInput type="checkbox" name="is_blockad" label="Blockad *"></FormulateInput>
                    <FormulateInput
                        id="huvudanestesikod"
                        name="huvudanestesikod"
                        label="Huvudanestesikod"
                        type="vueselect"
                        :options="huvudanestesikoder"
                        @search="searchHuvudanestesikod"
                        class="autocomplete"
                    />
                    <KRepeater
                        name="bianestesikoder"
                        add-label="Lägg till bianestesikod"
                        remove-label="Ta bort bianestesikod"
                        :converter="defaultRepeaterConverter"
                    >
                        <FormulateInput
                            id="bianestesikod"
                            name="bianestesikod"
                            label="Bianestesikod"
                            type="vueselect"
                            :options="bianestesikoderOptions"
                            @search="searchBianestesikod"
                            class="autocomplete"
                        ></FormulateInput
                    ></KRepeater>
                    <FormulateInput
                        type="knumber"
                        name="ordning"
                        label="Ordningsnummer *"
                        validation="required"
                        validation-name="Ordningsnummer"
                    >
                    </FormulateInput>
                </div>
                <ButtonGroup>
                    <CustomButton type="submit">
                        <span>{{ props.parameters.redigera ? "Redigera anestesi" : "Skapa ny anestesi" }}</span>
                    </CustomButton>
                    <CustomButton @click.prevent="cancelForm" size="slim" variant="contour">
                        <span>Avbryt</span>
                    </CustomButton>
                </ButtonGroup>
            </FormulateForm>
        </div>
    </div>
</template>

<script>
    import tabIcon from "@/assets/receptforskrivning.svg";
    import KRepeater from "@/components/forms/groups/Repeater/KRepeater.vue";

    export default {
        name: "Anestesi",
        tabName: "Anestesi",
        icon: tabIcon,
    };
</script>

<script setup>
    import { ref, useAttrs, toRefs, computed } from "vue";
    import emitter from "tiny-emitter/instance";

    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import { getConfig, klinikenApi } from "@/api";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { flashMessage, openDialog } from "@/utils";
    import CustomButton from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import store from "@/store";
    import { cvOptions, fetchData } from "@/tabs/Operation/utils";
    import { onMounted } from "vue";
    import { defaultRepeaterConverter } from "@/components/forms/groups/Repeater/helpers";
    import cloneDeep from "lodash/cloneDeep";

    const attrs = useAttrs();

    const props = defineProps({
        parameters: Object,
    });

    const { parameters } = toRefs(props);

    const formData = ref({
        namn: "",
        ordning: "",
        active: "ja",
        ...parameters.value,
    });
    const defaultFormData = ref({ ...formData.value });
    const dialog = ref();
    const huvudanestesikoder = ref([]);
    const bianestesikoderOptions = ref([]);

    const cancelForm = async () => {
        if (!formIsChanged.value) {
            store.dispatch("tabs/closeTab", attrs["tab-id"]);
        } else {
            const confirm = await dialog.value.show({
                variant: BasicModalVariant.WARNING,
                title: "Ändringar har gjorts",
                message: "Vill du spara ändringarna?",
                cancelButton: "Avbryt",
                okButton: "Ja",
                cancelButtonVariant: "contour",
            });
            if (confirm) {
                store.dispatch("tabs/closeTab", attrs["tab-id"]);
            } else {
                return;
            }
        }
    };

    const formIsChanged = computed(() => {
        return JSON.stringify(formData.value) !== JSON.stringify(defaultFormData.value);
    });

    const createAnestesi = async () => {
        let payload = cloneDeep(formData.value);

        if (payload.bianestesikoder) {
            payload.bianestesikoder = payload.bianestesikoder
                .filter((item) => {
                    return item !== null && item.bianestesikod;
                })
                .map((item) => {
                    if (item.bianestesikod && typeof item.bianestesikod === "object") {
                        return item.bianestesikod.value;
                    } else {
                        return item.bianestesikod;
                    }
                });
        }

        if (payload.huvudanestesikod && typeof payload.huvudanestesikod === "object") {
            payload.huvudanestesikod = payload.huvudanestesikod.value;
        }

        try {
            if (parameters.value.redigera) {
                const response = await klinikenApi.put(
                    `/operation/kodverk/anestesier/${parameters.value.id}/`,
                    payload,
                    getConfig()
                );
                if (response.status === 200) {
                    flashMessage("Redigerat");
                }
                await store.dispatch("tabs/closeTab", attrs["tab-id"]);
            } else {
                const response = await klinikenApi.post("/operation/kodverk/anestesier/", payload, getConfig());
                if (response.status === 201) {
                    flashMessage("Anestesi skapad");
                }
                await store.dispatch("tabs/closeTab", attrs["tab-id"]);
            }
        } catch (error) {
            openDialog(
                parameters.value.redigera
                    ? "Något gick fel, kunde inte uppdatera anestesi. "
                    : "Något gick fel, kunde inte skapa anestesi",
                "warning"
            );
        }
        emitter.emit("Anestesi-table-uppdaterad", {});
    };

    onMounted(async () => {
        let anestesikoder = cvOptions(
            await fetchData({
                url: "/operation/kodverk/anestesikoder/",
            })
        );

        huvudanestesikoder.value = anestesikoder;
        bianestesikoderOptions.value = huvudanestesikoder.value;

        // Det finns för många anestesikoder för att kunna använda
        // id i form-fältet så att den matcher emot sina options
        // (ser ut att vara samma i operationsjournal) så jag kopierade det här
        // ifrån operationsjournalen. Det sätter värdet i form-fältet
        // från _display-värdet från backend
        if (parameters.value.huvudanestesikod_display) {
            const formatted = `${props.parameters.huvudanestesikod_display.code} ${props.parameters.huvudanestesikod_display.displayName}`;
            formData.value["huvudanestesikod"] = {
                value: props.parameters.huvudanestesikod,
                label: formatted,
            };
        }

        if (parameters.value.bianestesikoder_display && props.parameters.bianestesikoder.length > 0) {
            formData.value["bianestesikoder"] = props.parameters.bianestesikoder_display.map((display, index) => {
                const formattedLabel = `${display.code} ${display.displayName}`;
                return {
                    bianestesikod: {
                        value: props.parameters.bianestesikoder[index],
                        label: formattedLabel,
                    },
                };
            });
        }
    });

    const searchHuvudanestesikod = async (search, loading) => {
        if (search.trim().length >= 3) {
            loading(true);
            huvudanestesikoder.value = cvOptions(
                await fetchData({
                    url: "/operation/kodverk/anestesikoder/all/",
                    params: { q: search },
                })
            );
            loading(false);
        }
    };

    const searchBianestesikod = async (search, loading) => {
        if (search.trim().length >= 3) {
            loading(true);
            bianestesikoderOptions.value = cvOptions(
                await fetchData({
                    url: "/operation/kodverk/anestesikoder/all/",
                    params: { q: search },
                })
            );
            loading(false);
        }
    };
</script>
<style lang="scss">
    .anestesi {
        .form {
            h1 {
                margin: 0;
            }

            .grid-container {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                gap: 10px;
            }
        }
    }
</style>
